import { Box, Paper, Avatar, Typography, Chip, Tabs, Tab } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import WorkIcon from "@mui/icons-material/Work";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import SecurityIcon from "@mui/icons-material/Security";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const getInitials = (firstName, lastName) => {
  if (!firstName || !lastName) return "U";
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

function ProfileOverviewCard({
  userDetails,
  avatarKey,
  activeTab,
  onTabChange,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        background: (theme) =>
          theme.palette.mode === "light"
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
      }}
    >
      {/* Profile Header */}
      <Box
        sx={{
          position: "relative",
          background: (theme) =>
            theme.palette.mode === "light"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          p: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "flex-start" },
            gap: { xs: 3, md: 4 },
          }}
        >
          {/* Avatar Section */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar
              key={avatarKey}
              src={userDetails?.avatar?.url || undefined}
              slotProps={{
                img: { loading: "eager" },
              }}
              alt={t("userProfile.avatarAlt", {
                username:
                  userDetails?.username || t("userProfile.unknownUsername"),
              })}
              sx={{
                width: { xs: 100, sm: 120, md: 140 },
                height: { xs: 100, sm: 120, md: 140 },
                border: (theme) =>
                  `4px solid ${theme.palette.background.paper}`,
                boxShadow: (theme) => theme.shadows[8],
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                fontWeight: 700,
                background: (theme) =>
                  theme.palette.mode === "light"
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.05)})`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.primary.dark, 0.1)})`,
              }}
              onError={(e) => {
                e.currentTarget.src = "";
              }}
            >
              {getInitials(userDetails?.first_name, userDetails?.last_name)}
            </Avatar>
          </Box>

          {/* User Information */}
          <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                color: "text.primary",
              }}
            >
              {userDetails?.first_name || t("userProfile.unknownName")}{" "}
              {userDetails?.last_name || t("userProfile.unknownName")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 2,
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <Chip
                icon={<WorkIcon />}
                label={
                  userDetails?.department?.name ||
                  t("userProfile.noDepartmentText")
                }
                variant="outlined"
                color="primary"
                sx={{
                  borderRadius: 2,
                  fontWeight: 500,
                  "& .MuiChip-icon": {
                    fontSize: 16,
                  },
                }}
              />
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <EmailIcon sx={{ fontSize: 18 }} />
              {userDetails?.email || t("userProfile.unknownEmail")}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: 16 }} />
              {t("userProfile.memberSince", "Member since")}{" "}
              {convertUTCToLocal(userDetails?.created_at) ||
                t("userProfile.unknownDate")}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs Navigation */}
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          px: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => onTabChange(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9375rem",
              minHeight: 48,
              "&.Mui-selected": {
                color: "primary.main",
              },
            },
          }}
        >
          <Tab
            icon={<PersonIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={t("userProfile.tabPersonal", "Personal Info")}
          />
          <Tab
            icon={<BusinessIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={t("userProfile.tabWorkplace", "Workplace")}
          />
          <Tab
            icon={<BadgeIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={t("userProfile.tabCertificates", "Certificates")}
          />
          <Tab
            icon={<SecurityIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={t("userProfile.tabSecurity", "Security")}
          />
        </Tabs>
      </Box>
    </Paper>
  );
}

ProfileOverviewCard.propTypes = {
  userDetails: PropTypes.object,
  avatarKey: PropTypes.string.isRequired,
  activeTab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

export default ProfileOverviewCard;
